import type { SceneTier } from '@cfg/shared';

export interface TierLimits {
  /** Largest tier width the caller allows (e.g. 1280 on phones). */
  maxTierWidth?: number;
  /** GPU texture size limit. */
  maxTextureSize: number;
}

/** Tiers that respect the limits, smallest first (the smallest tier if none does). */
export function allowedTiers(tiers: readonly SceneTier[], limits: TierLimits): SceneTier[] {
  const sorted = [...tiers].sort((a, b) => a.width - b.width);
  const maxWidth = Math.min(limits.maxTierWidth ?? Infinity, limits.maxTextureSize);
  const allowed = sorted.filter(
    (t) => t.width <= maxWidth && Math.max(t.width, t.height) <= limits.maxTextureSize,
  );
  return allowed.length ? allowed : sorted.slice(0, 1);
}

/** The smallest allowed tier that is at least `neededWidth` px wide (else the largest allowed). */
export function pickSceneTier(
  tiers: readonly SceneTier[],
  neededWidth: number,
  limits: TierLimits,
): SceneTier {
  const allowed = allowedTiers(tiers, limits);
  return allowed.find((t) => t.width >= neededWidth) ?? allowed[allowed.length - 1]!;
}

/** The largest allowed tier, optionally capped at `maxWidth`. */
export function largestSceneTier(
  tiers: readonly SceneTier[],
  limits: TierLimits,
  maxWidth = Infinity,
): SceneTier {
  const allowed = allowedTiers(tiers, limits);
  return [...allowed].reverse().find((t) => t.width <= maxWidth) ?? allowed[0]!;
}

/**
 * Device pixels the scene spans horizontally, i.e. the tier width needed to show it sharply:
 * the fitted scene width × pixel ratio × zoom (a 4× zoom shows a quarter of the scene
 * across the same canvas).
 */
export function neededSceneWidth(input: {
  canvasWidth: number;
  canvasHeight: number;
  /** Scene width / height. */
  sceneAspect: number;
  fit: 'contain' | 'cover';
  pixelRatio: number;
  zoom: number;
  /** Lower bound (default 640). */
  minWidth?: number;
}): number {
  const { canvasWidth: cw, canvasHeight: ch, sceneAspect } = input;
  const byHeight = ch > 0 ? ch * sceneAspect : cw;
  const shown = input.fit === 'cover' ? Math.max(cw, byHeight) : Math.min(cw, byHeight);
  return Math.max(input.minWidth ?? 640, shown * input.pixelRatio * Math.max(1, input.zoom));
}
